import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useTenant } from './TenantContext';
import { useAuth } from './AuthContext';
import { useI18n } from './I18nContext';
import { getPublicBranding } from '../modules/branding/api/brandingApi';
import { applyBrandColor } from '../modules/branding/lib/colorMath';

const BrandingContext = createContext(null);

const EMPTY_PUBLIC_BRANDING = {
  tenantName: null, logoUrl: null, primaryColor: null, welcomeMessage: null,
  backgroundImageId: null, overlayColor: null, overlayOpacity: null,
};

/**
 * Applies tenant branding (primary color as CSS custom properties, plus
 * exposing logo/welcome-message/background-image for LoginPage) both
 * before and after login, through the same applyBrandColor() call either
 * way -- see colorMath.js.
 *
 * Pre-login: fetches the public branding payload for TenantContext's
 * guessed tenant slug (see apps/api/src/modules/branding/public-routes.js).
 * Post-login: reads logoUrl/primaryColor directly off AuthContext's
 * already-extended `tenant` object (no second fetch -- see
 * apps/api/src/lib/session.js's buildMePayload).
 */
export function BrandingProvider({ children }) {
  const { tenantSlug } = useTenant();
  const { isAuthenticated, tenant } = useAuth();
  const { language } = useI18n();

  const [publicBranding, setPublicBranding] = useState(EMPTY_PUBLIC_BRANDING);

  useEffect(() => {
    if (isAuthenticated) return undefined; // post-login path below handles color instead
    let cancelled = false;

    getPublicBranding(tenantSlug, language)
      .then((payload) => {
        if (!cancelled) setPublicBranding(payload || EMPTY_PUBLIC_BRANDING);
      })
      .catch(() => {
        if (!cancelled) setPublicBranding(EMPTY_PUBLIC_BRANDING);
      });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, tenantSlug, language]);

  useEffect(() => {
    if (isAuthenticated) {
      applyBrandColor(tenant?.primaryColor);
    } else {
      applyBrandColor(publicBranding.primaryColor);
    }
  }, [isAuthenticated, tenant?.primaryColor, publicBranding.primaryColor]);

  const value = useMemo(() => ({ ...publicBranding }), [publicBranding]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used within BrandingProvider');
  return ctx;
}
